import React, { useCallback } from 'react';
import { SparkFlowStep } from '../types/sparkTypes';
import { useAtlasStore } from '../../../store/atlasStore';
import { InlineSvg } from '../../../components/icons/InlineSvg';
import styles from '../styles/ProjectSidebar.module.css';

interface ProjectSidebarProps {
  steps: SparkFlowStep[];
  activeIndex: number;
  completedSteps?: boolean[];
  onSelect: (index: number) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

// Icons for sidebar items
const SetupIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

export const BriefingIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14,2 14,8 20,8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10,9 9,9 8,9" />
  </svg>
);

const ResourcesIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
  </svg>
);

// Sidebar toggle icon - two overlapping panels (matches reference screenshot)
const SidebarToggleIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M9 3v18" />
  </svg>
);

export const ProjectSidebar: React.FC<ProjectSidebarProps> = ({
  steps,
  activeIndex,
  completedSteps = [],
  onSelect,
  isCollapsed,
  onToggleCollapse,
}) => {
  // Get creative types from store for icon lookup
  const sparkCreativeTypes = useAtlasStore((state) => state.sparkCreativeTypes);

  // Split steps into groups
  const setupSteps = steps.slice(0, 3); // First three steps: Project Setup, Briefing, Insights
  const innovationSteps = steps.slice(3); // Remaining dynamic steps

  const getStepIcon = (step: SparkFlowStep, index: number) => {
    // Use step name to determine icon type for more robust matching
    if (step.name === 'Project Setup' || index === 0) return <SetupIcon />;
    if (step.name === 'Briefing' || index === 1) return <BriefingIcon />;
    if (step.name === 'Understanding' || index === 2) return <ResourcesIcon />;

    // For innovation steps, check for custom icon from creative type
    if (step.creative_type_fqn) {
      const creativeType = sparkCreativeTypes.find(ct => ct.fqn === String(step.creative_type_fqn));
      if (creativeType?.icon_url) {
        return <InlineSvg url={creativeType.icon_url} className={styles.customIcon} />;
      }
    }
    // No icon if creative type doesn't have one
    return null;
  };

  const handleItemClick = useCallback((index: number) => {
    onSelect(index);
  }, [onSelect]);

  return (
    <nav
      className={`${styles.sidebar} ${isCollapsed ? styles.collapsed : ''}`}
      aria-label="Project navigation"
    >
      {/* Sidebar toggle button */}
      <button
        className={styles.collapseButton}
        onClick={onToggleCollapse}
        aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        <SidebarToggleIcon />
      </button>

      {/* Navigation content - hidden when collapsed (only show toggle button) */}
      {!isCollapsed && (
        <div className={styles.navContent}>
          {/* Setup Group */}
          <div className={styles.navGroup}>
            <span className={styles.groupLabel}>Setup</span>
            <ul className={styles.navList}>
              {setupSteps.map((step, idx) => (
                <li key={step.name || idx}>
                  <button
                    className={`${styles.navItem} ${activeIndex === idx ? styles.active : ''}`}
                    onClick={() => handleItemClick(idx)}
                    aria-current={activeIndex === idx ? 'page' : undefined}
                  >
                    <span className={`${styles.navIcon} ${completedSteps[idx] ? styles.completed : ''}`}>{getStepIcon(step, idx)}</span>
                    <span className={styles.navLabel}>{step.label || step.name}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Innovation Group - only show if there are innovation steps */}
          {innovationSteps.length > 0 && (
            <div className={styles.navGroup}>
              <span className={styles.groupLabel}>Innovation</span>
              <ul className={styles.navList}>
                {innovationSteps.map((step, idx) => {
                  const actualIndex = idx + 3; // Offset by 3 setup steps
                  return (
                    <li key={step.name || actualIndex}>
                      <button
                        className={`${styles.navItem} ${activeIndex === actualIndex ? styles.active : ''}`}
                        onClick={() => handleItemClick(actualIndex)}
                        aria-current={activeIndex === actualIndex ? 'page' : undefined}
                      >
                        <span className={`${styles.navIcon} ${completedSteps[actualIndex] ? styles.completed : ''}`}>{getStepIcon(step, actualIndex)}</span>
                        <span className={styles.navLabel}>{step.label || step.name}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      )}
    </nav>
  );
};
